<template>
  <div class="page-container tool-page">
    <!-- 顶部统计卡片 -->
    <div class="stats-row">
      <div class="stat-item">
        <div class="stat-item-value">{{ stats.totalCount || 0 }}</div>
        <div class="stat-item-label">工具总数</div>
      </div>
      <div class="stat-item success">
        <div class="stat-item-value">{{ stats.availableCount || 0 }}</div>
        <div class="stat-item-label">可用</div>
      </div>
      <div class="stat-item info">
        <div class="stat-item-value">{{ stats.usingCount || 0 }}</div>
        <div class="stat-item-label">借出中</div>
      </div>
      <div class="stat-item warning">
        <div class="stat-item-value">{{ stats.pendingCount || 0 }}</div>
        <div class="stat-item-label">待审批</div>
      </div>
    </div>

    <div class="card">
      <div class="tabs-header-wrapper">
        <el-tabs v-model="activeTab" class="main-tabs" @tab-click="handleTabClick">
          <el-tab-pane label="工具台账" name="ledger"/>
          <el-tab-pane label="借用记录" name="borrow">
          <span slot="label" class="tab-label-with-badge">
              借用记录
              <el-badge
                v-if="(stats.workbenchTodoCount || 0) > 0"
                :max="99"
                :value="stats.workbenchTodoCount || 0"
                class="borrow-tab-badge"
              />
            </span>
          </el-tab-pane>
        </el-tabs>

        <div v-if="activeTab === 'ledger'" class="action-btn-group">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增工具</el-button>
          <el-button type="success" size="small" icon="el-icon-upload2" @click="handleImport">Excel导入</el-button>
        </div>
      </div>

      <!-- ===== Tab1: 工具台账 ===== -->
      <template v-if="activeTab === 'ledger'">
        <div class="search-bar">
          <el-input
            v-model="ledgerQuery.keyword"
            placeholder="工具名称/备注"
            clearable
            style="width: 200px"
            @change="handleLedgerQuery"
          />
          <dict-select
            v-model="ledgerQuery.toolType"
            :dict-type="DICT_TYPE.TOOL_TYPE"
            placeholder="工具类型"
            clearable
            style="width: 150px"
            @change="handleLedgerQuery"
          />
          <el-select
            v-model="ledgerQuery.status"
            placeholder="状态"
            clearable
            style="width: 120px"
            @change="handleLedgerQuery"
          >
            <el-option label="可借用" value="available" />
            <el-option label="已借完" value="empty" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleLedgerQuery">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="handleLedgerReset">重置</el-button>
        </div>

        <div class="table-wrapper">
          <el-table
            key="ledger-table"
            v-loading="loading"
            row-key="uuid"
            :data="ledgerData"
            border
            stripe
            style="width: 100%"
            height="100%"
            :header-cell-style="{ textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
          >
            <el-table-column label="工具编号" prop="toolCode" width="100" />
            <el-table-column label="工具名称" prop="toolName" min-width="100" show-overflow-tooltip />
            <el-table-column label="工具类型" prop="toolType" width="120">
              <template slot-scope="{ row }">
                <dict-tag :dict-type="DICT_TYPE.TOOL_TYPE" :value="row.toolType" />
              </template>
            </el-table-column>
            <el-table-column label="存放位置" prop="storageLocation" min-width="140" show-overflow-tooltip />
            <el-table-column label="备注" prop="remark" min-width="120" show-overflow-tooltip />
            <el-table-column label="可用/总数" width="200">
              <template slot-scope="{ row }">
                <div class="available-progress-cell">
                  <el-progress
                    :percentage="row.availableRatio || 0"
                    :status="progressStatus(row)"
                    :stroke-width="8"
                    :show-text="false"
                    style="flex: 1; margin-left: 8px"
                  />
                  <span class="available-text" :class="availableClass(row)">{{ row.availableCount }} / {{ row.totalCount }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" prop="status" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="row.status === 'available' ? 'success' : 'danger'" size="small">
                  {{ row.statusName || row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="270" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" plain size="mini" @click="openToolDetail(row)">查看详情</el-button>
                <el-button
                  type="warning"
                  plain
                  size="mini"
                  :disabled="!row || row.status !== 'available' || row.availableCount <= 0"
                  @click="handleBorrowApply(row)"
                >
                  借用申请
                </el-button>
                <el-button type="info" plain size="mini" @click="handleEdit(row)">编辑</el-button>
                <el-button type="danger" plain size="mini" @click="handleDelete(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-pagination
          class="pagination"
          :current-page="ledgerPage.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="ledgerPage.pageSize"
          :total="ledgerPage.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLedgerSizeChange"
          @current-change="handleLedgerPageChange"
        />
      </template>

      <!-- ===== Tab2: 借用记录 ===== -->
      <template v-if="activeTab === 'borrow'">
        <div class="search-bar search-bar--borrow">
          <span class="filter-label">快捷筛选</span>
          <el-radio-group
            v-model="borrowQuery.quickFilter"
            class="borrow-quick-group"
            size="small"
            @change="handleBorrowQuery"
          >
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="pending">待处理</el-radio-button>
            <el-radio-button label="using">使用中</el-radio-button>
            <el-radio-button label="returned">已归还</el-radio-button>
            <el-radio-button label="rejected">已拒绝</el-radio-button>
          </el-radio-group>
          <div class="borrow-extra-filters">
            <el-input
              v-model="borrowQuery.keyword"
              placeholder="工具名称/用途说明"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleBorrowDetailSearch"
            />
            <el-input
              v-model="borrowQuery.borrowUserKeyword"
              placeholder="借用人"
              clearable
              style="width: 140px"
              @keyup.enter.native="handleBorrowDetailSearch"
            />
            <el-date-picker
              v-model="borrowApplyDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="申请时间起"
              end-placeholder="申请时间止"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              style="width: 280px"
              @change="handleBorrowApplyDateChange"
            />
            <el-button type="primary" size="small" icon="el-icon-search" @click="handleBorrowDetailSearch">搜索</el-button>
            <el-button size="small" icon="el-icon-refresh" @click="handleBorrowDetailReset">重置</el-button>
          </div>
        </div>
        <div class="table-wrapper">
          <el-table
            key="borrow-table"
            v-loading="loading"
            row-key="uuid"
            :data="borrowData"
            border
            stripe
            style="width: 100%"
            height="100%"
            :header-cell-style="{ textAlign: 'center' }"
            :cell-style="{ textAlign: 'center' }"
          >
            <el-table-column label="工具编号" prop="toolCode" width="100" />
            <el-table-column label="工具名称" prop="toolName" min-width="100" show-overflow-tooltip />
            <el-table-column label="借用数量" prop="borrowCount" width="80" />
            <el-table-column label="用途说明" prop="purpose" min-width="150" show-overflow-tooltip />
            <el-table-column label="借用人" prop="borrowUserName" width="100" />
            <el-table-column label="申请时间" prop="applyTime" width="150">
              <template slot-scope="{ row }">{{ formatDateTime(row.applyTime) }}</template>
            </el-table-column>
            <el-table-column label="预计归还时间" prop="expectReturnTime" width="150">
              <template slot-scope="{ row }">{{ formatDateTime(row.expectReturnTime) }}</template>
            </el-table-column>
            <el-table-column label="状态" prop="status" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="borrowStatusType(row.status)" size="small">{{ row.statusName || row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="审批备注" prop="remark" min-width="150" show-overflow-tooltip />
            <el-table-column label="操作" width="200" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="primary" plain size="mini" @click="openBorrowDetail(row)">查看详情</el-button>
                <template v-if="row.status === 'pending'">
                  <el-button type="success" plain size="mini" @click="handleApprove(row)">通过</el-button>
                  <el-button type="danger" plain size="mini" @click="handleReject(row)">拒绝</el-button>
                </template>
                <template v-else-if="row.status === 'return_pending'">
                  <el-button type="warning" plain size="mini" @click="handleConfirmReturn(row)">确认归还</el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <el-pagination
          class="pagination"
          :current-page="borrowPage.pageNo"
          :page-sizes="[10, 20, 50]"
          :page-size="borrowPage.pageSize"
          :total="borrowPage.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleBorrowSizeChange"
          @current-change="handleBorrowPageChange"
        />
      </template>
    </div>

    <!-- 新增/编辑工具弹窗 -->
    <ToolDialog ref="toolDialog" @success="handleDialogSuccess" />

    <!-- 借用申请弹窗 -->
    <BorrowApplyDialog ref="borrowApplyDialog" @success="handleDialogSuccess" />

    <!-- 详情弹窗 -->
    <ToolDetailDialog
      v-if="toolDetailVisible"
      :visible.sync="toolDetailVisible"
      :detail="toolDetailData"
      :mode="toolDetailMode"
    />

    <!-- 导入工具弹窗 -->
    <ImportToolDialog :visible.sync="importVisible" @success="handleImportSuccess" />
  </div>
</template>

<script>
// 服务
import { toolService } from './tool.service';
// 工具函数
import { DICT_TYPE } from '@/features/components/dict/dictTypeKeys';
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
// 组件
import ToolDialog from './toolDialog.vue';
import BorrowApplyDialog from './borrowApplyDialog.vue';
import ToolDetailDialog from './toolDetailDialog.vue';
import ImportToolDialog from './importToolDialog.vue';

export default {
  name: 'ToolPage',
  components: { ToolDialog, BorrowApplyDialog, ToolDetailDialog, ImportToolDialog },
  data() {
    return {
      activeTab: 'ledger',
      loading: false,
      stats: {},

      DICT_TYPE,
      // Tab1 工具台账
      ledgerQuery: { keyword: '', toolType: '', status: '' },
      ledgerData: [],
      ledgerPage: { pageNo: 1, pageSize: 10, total: 0 },

      // Tab2 借用记录（快捷筛选 + 与台账一致的细项条件）
      borrowQuery: {
        quickFilter: 'all',
        keyword: '',
        borrowUserKeyword: '',
        applyTimeStart: null,
        applyTimeEnd: null
      },
      borrowApplyDateRange: null,
      borrowData: [],
      borrowPage: { pageNo: 1, pageSize: 10, total: 0 },

      toolDetailVisible: false,
      toolDetailData: null,
      toolDetailMode: 'tool',

      importVisible: false
    };
  },
  created() {
    this.loadLedger();
    this.loadStats();
  },
  watch: {
    activeTab(tab) {
      if (tab === 'ledger') this.loadLedger();
      else if (tab === 'borrow') this.loadBorrow();
      this.loadStats();
    }
  },
  methods: {
    handleTabClick(tab) {
      if (tab && tab.name) {
        this.activeTab = tab.name;
      }
      if (tab.name === 'borrow') {
        this.borrowQuery.quickFilter = this.stats.workbenchTodoCount > 0 ? 'pending' : 'all';
      }
      this.loadBorrow();
    },

    // ===== 统计 =====
    loadStats() {
      toolService.getStatistics().then((res) => {
        this.stats = res.data || {};
      });
    },

    // ===== Tab1 工具台账 =====
    async loadLedger() {
      this.loading = true;
      const params = {
        pageNo: this.ledgerPage.pageNo,
        pageSize: this.ledgerPage.pageSize,
        keyword: this.ledgerQuery.keyword || undefined,
        toolType: this.ledgerQuery.toolType || undefined,
        status: this.ledgerQuery.status || undefined
      };
      toolService.queryToolPage(params).then((res) => {
        const page = res.data || {};
        this.ledgerData = page.rows || [];
        this.ledgerPage.total = page.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleLedgerQuery() {
      this.ledgerPage.pageNo = 1;
      this.loadLedger();
      this.loadStats();
    },
    handleLedgerReset() {
      this.ledgerQuery = { keyword: '', toolType: '', status: '' };
      this.ledgerPage.pageNo = 1;
      this.loadLedger();
    },
    handleImport() {
      this.importVisible = true;
    },
    handleImportSuccess() {
      this.$message.success('导入完成');
      this.loadLedger();
      this.loadStats();
    },
    handleLedgerSizeChange(size) {
      this.ledgerPage.pageSize = size;
      this.loadLedger();
    },
    handleLedgerPageChange(page) {
      this.ledgerPage.pageNo = page;
      this.loadLedger();
    },
    openToolDetail(row) {
      this.toolDetailMode = 'tool';
      this.toolDetailData = row;
      this.toolDetailVisible = true;
    },
    openBorrowDetail(row) {
      this.toolDetailMode = 'borrow';
      this.toolDetailData = row;
      this.toolDetailVisible = true;
    },
    handleAdd() {
      this.$refs.toolDialog.open(null);
    },
    handleEdit(row) {
      this.$refs.toolDialog.open(row);
    },
    handleBorrowApply(row) {
      this.$refs.borrowApplyDialog.open(row);
    },
    handleDelete(row) {
      this.$confirm(`确定删除工具「${row.toolName}」？删除后不可恢复。`, '提示', {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }).then(async () => {
        this.loading = true;
        await toolService.deleteTool(row.uuid);
        this.$message.success('删除成功');
        this.loadLedger();
        this.loadStats();
      }).catch(() => {}).finally(() => {
        this.loading = false;
      });
    },
    handleDialogSuccess() {
      if (this.activeTab === 'ledger') this.loadLedger();
      else if (this.activeTab === 'borrow') this.loadBorrow();
      this.loadStats();
    },
    async loadBorrow() {
      this.loading = true;
      const params = {
        pageNo: this.borrowPage.pageNo,
        pageSize: this.borrowPage.pageSize,
        keyword: this.borrowQuery.keyword || undefined,
        borrowUserKeyword: this.borrowQuery.borrowUserKeyword || undefined,
        applyTimeStart: this.borrowQuery.applyTimeStart || undefined,
        applyTimeEnd: this.borrowQuery.applyTimeEnd || undefined
      };
      const q = this.borrowQuery.quickFilter;
      if (q === 'pending') {
        params.statusList = ['pending', 'return_pending'];
      } else if (q === 'using') {
        params.statusList = ['using'];
      } else if (q === 'returned') {
        params.statusList = ['returned'];
      } else if (q === 'rejected') {
        params.statusList = ['rejected'];
      }
      toolService.queryBorrowPage(params).then((res) => {
        const page = res.data || {};
        this.borrowData = page.rows || [];
        this.borrowPage.total = page.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    handleBorrowQuery() {
      this.borrowPage.pageNo = 1;
      this.loadBorrow();
    },
    handleBorrowDetailSearch() {
      this.borrowPage.pageNo = 1;
      this.loadBorrow();
    },
    handleBorrowDetailReset() {
      this.borrowQuery.keyword = '';
      this.borrowQuery.borrowUserKeyword = '';
      this.borrowQuery.applyTimeStart = null;
      this.borrowQuery.applyTimeEnd = null;
      this.borrowApplyDateRange = null;
      this.borrowPage.pageNo = 1;
      this.loadBorrow();
    },
    handleBorrowApplyDateChange(val) {
      if (val && val.length === 2) {
        this.borrowQuery.applyTimeStart = val[0];
        this.borrowQuery.applyTimeEnd = val[1];
      } else {
        this.borrowQuery.applyTimeStart = null;
        this.borrowQuery.applyTimeEnd = null;
      }
      this.handleBorrowDetailSearch();
    },
    handleBorrowSizeChange(size) {
      this.borrowPage.pageSize = size;
      this.loadBorrow();
    },
    handleBorrowPageChange(page) {
      this.borrowPage.pageNo = page;
      this.loadBorrow();
    },
    // 审批通过
    handleApprove(row) {
      this.$confirm(`确定审批通过「${row.toolName}」的借用申请（数量：${row.borrowCount}）？\n通过后可用数量立即扣减。`, '审批确认', {
        type: 'warning',
        confirmButtonText: '确定通过',
        cancelButtonText: '取消'
      }).then(() => {
        this.loading = true;
        toolService.approveBorrow({ uuid: row.uuid }).then(() => {
          this.$message.success('审批通过');
          this.loadBorrow();
          this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      });
    },
    // 审批拒绝
    handleReject(row) {
      this.$prompt('请输入拒绝原因', '审批拒绝', {
        confirmButtonText: '确认拒绝',
        cancelButtonText: '取消',
        inputPlaceholder: '请填写拒绝原因（必填）',
        inputValidator: (value) => {
          const v = (value || '').trim();
          if (!v) {
            return '请填写拒绝原因';
          }
          return true;
        },
        inputErrorMessage: '请填写拒绝原因'
      }).then(({ value }) => {
        this.loading = true;
        const remark = (value || '').trim();
        toolService.rejectBorrow({ uuid: row.uuid, remark }).then(() => {
          this.$message.success('已拒绝');
          this.loadBorrow();
          this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      });
    },
    // 确认归还
    handleConfirmReturn(row) {
      this.$confirm(`确定确认归还「${row.toolName}」（借用人：${row.borrowUserName}，数量：${row.borrowCount}）？\n归还后可用数量将恢复。`, '确认归还', {
        type: 'warning',
        confirmButtonText: '确认归还',
        cancelButtonText: '取消'
      }).then(() => {
        this.loading = true;
        toolService.confirmReturn({ uuid: row.uuid }).then(() => {
          this.$message.success('归还确认成功，库存已恢复');
          this.loadBorrow();
          this.loadStats();
        }).finally(() => {
          this.loading = false;
        });
      });
    },

    // ===== 工具方法 =====
    progressStatus(row) {
      const ratio = row.availableRatio || 0;
      if (ratio >= 60) return null;
      if (ratio >= 20) return 'warning';
      return 'exception';
    },
    availableClass(row) {
      const ratio = row.availableRatio || 0;
      if (ratio >= 60) return 'text-success';
      if (ratio >= 20) return 'text-warning';
      return 'text-danger';
    },
    borrowStatusType(status) {
      const map = {
        pending: 'warning',
        using: 'primary',
        return_pending: 'warning',
        returned: 'success',
        rejected: 'danger'
      };
      return map[status] || 'info';
    },
    formatDateTime
  }
};
</script>

<style lang="scss" scoped>
.tool-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .stats-row {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
    margin-bottom: 10px;

    .stat-item {
      background: #fff;
      border-radius: 8px;
      padding: 18px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

      .stat-item-value {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 5px;
        color: #303133;
      }

      .stat-item-label {
        font-size: 14px;
        color: #909399;
      }

      &.success .stat-item-value { color: #67c23a; }
      &.info .stat-item-value { color: #409eff; }
      &.warning .stat-item-value { color: #e6a23c; }
      &.danger .stat-item-value { color: #f56c6c; }
    }
  }

  .card {
    flex: 1;
    min-height: 0;
    background: #fff;
    border-radius: 8px;
    padding: 10px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .tabs-header-wrapper {
      position: relative;

      ::v-deep .el-tabs__header {
        margin-bottom: 10px;
      }

      ::v-deep .el-tabs__nav-wrap::after {
        height: 1px;
        background-color: #ebeef5;
      }

      ::v-deep .el-tabs__item {
        font-size: 15px;
        font-weight: 500;
        height: 45px;
        line-height: 45px;
      }

      .action-btn-group {
        position: absolute;
        right: 0;
        top: 8px;
        display: flex;
        gap: 10px;
        z-index: 1;
      }

      .tab-label-with-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .borrow-tab-badge {
        vertical-align: middle;
        margin-top: -4px;
        ::v-deep .el-badge__content {
          border: none;
        }
      }
    }

    .search-bar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;
    }

    .search-bar--borrow {
      align-items: center;

      .filter-label {
        flex-shrink: 0;
        font-size: 13px;
        color: #606266;
      }

      .borrow-quick-group {
        flex-shrink: 0;
        flex-wrap: wrap;
      }

      .borrow-extra-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        margin-left: auto;
      }
    }

    .table-wrapper {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  }

  .pagination {
    flex-shrink: 0;
    margin-top: 20px;
    text-align: right;
  }
}

.available-progress-cell {
  display: flex;
  align-items: center;
  padding: 0 4px;
}
.available-text {
  font-size: 13px;
  white-space: nowrap;
  min-width: 54px;
}
.text-success { color: #67c23a; font-weight: 600; }
.text-warning { color: #e6a23c; font-weight: 600; }
.text-danger { color: #f56c6c; font-weight: 600; }
.no-action { color: #c0c4cc; }
</style>
